import React, { useMemo } from 'react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const loadRevenue = (l) =>
  ((l.customer_line_haul || 0) + (l.customer_accessorials || 0) + (l.fsc_enabled ? (l.fsc_amount || 0) : 0)) || l.customer_rate || 0;
const loadCost = (l) =>
  ((l.carrier_line_haul || 0) + (l.carrier_accessorials || 0)) || l.carrier_pay || l.carrier_rate || 0;

export const TrendChart = ({ loads }) => {
  const data = useMemo(() => {
    const now = new Date();
    const monday = new Date(now);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));

    const weeks = [];
    for (let i = 7; i >= 0; i--) {
      const start = new Date(monday.getTime() - i * 7 * 24 * 3600 * 1000);
      const end = new Date(start.getTime() + 7 * 24 * 3600 * 1000);
      weeks.push({
        start,
        end,
        label: start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        Revenue: 0,
        Margin: 0,
        loads: 0,
      });
    }

    (loads || []).forEach((l) => {
      const firstPickup = (l.stops || [])
        .filter((s) => s.type === 'pickup' && s.appointment_datetime)
        .map((s) => new Date(s.appointment_datetime))
        .sort((a, b) => a - b)[0];
      const dt = firstPickup || (l.created_at ? new Date(l.created_at) : null);
      if (!dt) return;
      const wk = weeks.find((w) => dt >= w.start && dt < w.end);
      if (!wk) return;
      const rev = loadRevenue(l);
      wk.Revenue += rev;
      wk.Margin += rev - loadCost(l);
      wk.loads += 1;
    });

    return weeks.map((w) => ({
      ...w,
      Revenue: Math.round(w.Revenue),
      Margin: Math.round(w.Margin),
    }));
  }, [loads]);

  return (
    <div className="stats-card mb-4 sm:mb-6 lg:mb-8" data-testid="trend-chart-widget">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-[var(--tms-text)]">Revenue & Margin — Last 8 Weeks</h3>
        <span className="text-xs text-[var(--tms-text-muted)]">by pickup week</span>
      </div>
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#f2f4f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#7987a1' }} axisLine={{ stroke: '#e8ebf1' }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#7987a1' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
            <Tooltip
              formatter={(v, name) => [`$${Number(v).toLocaleString()}`, name]}
              labelFormatter={(label, payload) => `Week of ${label}${payload?.[0] ? ` • ${payload[0].payload.loads} loads` : ''}`}
              contentStyle={{ fontSize: 12, border: '1px solid #f2f4f9', boxShadow: '0 5px 10px 0 rgba(183,192,206,.2)', borderRadius: 4 }}
            />
            <Bar dataKey="Revenue" fill="#d3d7f8" radius={[3, 3, 0, 0]} maxBarSize={34} />
            <Line type="monotone" dataKey="Margin" stroke="#F8191B" strokeWidth={2} dot={{ r: 3, fill: '#F8191B' }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
